import React, { useState } from "react";
import {
  Modal,
  Button,
  Form,
  Input,
  Select,
  Upload,
  message,
  Spin,
  Row,
  Col,
} from "antd";
import { InboxOutlined, PlusOutlined } from "@ant-design/icons";
import { createClient } from "../api/client/clientRequest";
import { CLIENT_TYPES } from "../utils/constants";
import { getMessageError } from "../utils/messageUtils";
import { handleFilename } from "../utils";
import PermissionRender from "./PermissionRender";

const { Option } = Select;
const { Dragger } = Upload;

const ClientCreateModal = ({ onClientCreated, publicInstitutions }) => {
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState([]);
  const [clientType, setClientType] = useState("individuals");

  const handleOpen = () => {
    setFileList([]);
    setClientType("individuals");
    form.resetFields();
    setVisible(true);
  };
  const handleClose = () => {
    form.resetFields();
    setFileList([]);
    setVisible(false);
  };

  const onTypeChange = (value) => {
    setClientType(value);
    form.setFieldsValue({
      cin: undefined,
      ice: undefined,
      rc: undefined,
      public_institution_id: undefined,
      contact_first_name: undefined,
      contact_last_name: undefined,
      contact_phone: undefined,
    });
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const formData = new FormData();
      Object.keys(values).forEach((key) => {
        if (
          values[key] !== undefined &&
          values[key] !== null &&
          values[key] !== ""
        ) {
          formData.append(key, values[key]);
        }
      });

      fileList.forEach((file) => {
        const processedFile = handleFilename(file);
        formData.append("files[]", processedFile);
      });

      const response = await createClient(formData);
      message.success("Client créé avec succès");
      onClientCreated(response);
      handleClose();
    } catch (error) {
      getMessageError(
        error.response?.data?.error,
        "Erreur lors de la création du client"
      );
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const uploadProps = {
    multiple: true,
    onRemove: (file) => {
      const index = fileList.indexOf(file);
      const newFileList = fileList.slice();
      newFileList.splice(index, 1);
      setFileList(newFileList);
    },
    beforeUpload: (file) => {
      const isValidType = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "image/jpeg",
        "image/png",
        "image/gif",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "text/plain",
      ].includes(file.type);

      const isLessThan10MB = file.size / 1024 / 1024 < 10;

      if (!isValidType) {
        message.error(`${file.name} n'est pas un type de fichier supporté`);
        return Upload.LIST_IGNORE;
      }

      if (!isLessThan10MB) {
        message.error(`${file.name} dépasse 10MB`);
        return Upload.LIST_IGNORE;
      }

      const processedFile = handleFilename(file);

      if (file.name !== processedFile.name) {
        message.info(
          `Fichier "${file.name}" renommé en "${processedFile.name}"`
        );
      }

      setFileList([...fileList, processedFile]);
      return false;
    },
    fileList,
  };

  return (
    <>
      <PermissionRender requiredPermission={["clients", "create"]}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleOpen}
          style={{ borderRadius: "8px" }}
        >
          Ajouter un Client
        </Button>
      </PermissionRender>
      <Modal
        title="Créer un Client"
        open={visible}
        onCancel={handleClose}
        footer={null}
        width={800}
      >
        <Spin spinning={loading}>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{ type: "individuals" }}
          >
            <Form.Item
              name="type"
              label="Type de client"
              rules={[{ required: true, message: "Le type est requis" }]}
            >
              <Select
                onChange={onTypeChange}
                placeholder="Sélectionner un type"
              >
                {Object.keys(CLIENT_TYPES).map((typeKey) => (
                  <Option key={typeKey} value={typeKey}>
                    {CLIENT_TYPES[typeKey]}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            {clientType === "individuals" && (
              <>
                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item name="civility" label="Civilité">
                      <Select placeholder="Sélectionner">
                        <Option value="Mr">Mr</Option>
                        <Option value="Mme">Mme</Option>
                        <Option value="Mlle">Mlle</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      name="first_name"
                      label="Prénom"
                      rules={[
                        { required: true, message: "Le prénom est requis" },
                      ]}
                    >
                      <Input placeholder="Prénom" />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      name="last_name"
                      label="Nom"
                      rules={[{ required: true, message: "Le nom est requis" }]}
                    >
                      <Input placeholder="Nom" />
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item
                  name="cin"
                  label="CIN"
                  rules={[{ required: true, message: "Le CIN est requis" }]}
                >
                  <Input placeholder="Entrez le CIN" />
                </Form.Item>
              </>
            )}

            {clientType === "companies" && (
              <>
                <Form.Item
                  name="name"
                  label="Nom de la société"
                  rules={[{ required: true, message: "Le nom est requis" }]}
                >
                  <Input placeholder="Nom de la société" />
                </Form.Item>
                <Form.Item
                  name="ice"
                  label="ICE"
                  rules={[{ required: true, message: "L'ICE est requis" }]}
                >
                  <Input placeholder="Entrez l'ICE" />
                </Form.Item>
                <Form.Item
                  name="rc"
                  label="RC"
                  // Add rules if RC is required for companies
                  // rules={[{ required: true, message: "Le RC est requis" }]}
                >
                  <Input placeholder="Entrez le numéro RC" />
                </Form.Item>
                <Row gutter={16}>
                  <Col span={24}>
                    <div className="text-md font-semibold mb-2">
                      Informations de Contact
                    </div>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="contact_first_name"
                      label="Prénom du Contact"
                    >
                      <Input placeholder="Prénom" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="contact_last_name" label="Nom du Contact">
                      <Input placeholder="Nom" />
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item name="contact_phone" label="Téléphone du Contact">
                  <Input placeholder="Téléphone" />
                </Form.Item>
              </>
            )}

            {clientType === "institutions" && (
              <>
                <Form.Item
                  name="name"
                  label="Nom de l'institution"
                  rules={[{ required: true, message: "Le nom est requis" }]}
                >
                  <Input placeholder="Nom de l'institution" />
                </Form.Item>
                <Form.Item
                  name="public_institution_id"
                  label="Organisme public"
                  rules={[
                    {
                      required: true,
                      message: "L'Organisme public est requis",
                    },
                  ]}
                >
                  <Select
                    placeholder="Sélectionner un Organisme public"
                    loading={!publicInstitutions?.length}
                    showSearch
                    filterOption={(input, option) =>
                      option.children
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  >
                    {publicInstitutions?.map((institution) => (
                      <Option key={institution.id} value={institution.id}>
                        {institution.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </>
            )}

            <Form.Item
              name="email"
              label="Email"
              rules={[{ type: "email", message: "Email invalide" }]}
            >
              <Input placeholder="Entrez l'email" />
            </Form.Item>
            <Form.Item
              name="phone"
              label="Téléphone Principal"
              rules={[
                {
                  required: true,
                  message: "Le numéro de téléphone est requis",
                },
                {
                  pattern: /^\+?[0-9\s-]+$/,
                  message: "Numéro de téléphone invalide",
                },
              ]}
            >
              <Input placeholder="Entrez le numéro de téléphone principal" />
            </Form.Item>
            <Form.Item
              name="address"
              label="Adresse Ligne 1"
              rules={[{ required: true, message: "L'adresse est requise" }]}
            >
              <Input placeholder="Adresse Ligne 1" />
            </Form.Item>
            <Form.Item name="address2" label="Adresse Ligne 2">
              <Input placeholder="Adresse Ligne 2 (optionnel)" />
            </Form.Item>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="city" label="Ville">
                  <Input placeholder="Ville" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="postal_code" label="Code Postal">
                  <Input placeholder="Code Postal" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="country" label="Pays">
                  <Input placeholder="Pays" />
                </Form.Item>
              </Col>
            </Row>

            <Dragger {...uploadProps} className="mb-4">
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">
                Cliquez ou glissez les fichiers ici
              </p>
              <p className="ant-upload-hint">
                Support pour PDF, Word, Excel, images (JPG, PNG, GIF) et TXT.
                Maximum 10MB par fichier.
              </p>
            </Dragger>

            <div className="mt-4">
              {fileList.length > 0 && (
                <p className="text-gray-600">
                  {fileList.length} fichier(s) prêt(s) à être envoyés
                </p>
              )}
            </div>

            <Form.Item className="mt-4">
              <Button type="primary" htmlType="submit" loading={loading} block>
                Créer
              </Button>
            </Form.Item>
          </Form>
        </Spin>
      </Modal>
    </>
  );
};

export default ClientCreateModal;
